import { useEffect, useState } from "react";
import { api, fmtDateTime } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, ShieldCheck } from "lucide-react";

const ROLE_LABELS = { admin: "Admin", gerente: "Gerente", supervisor: "Supervisor", operador: "Operador", caixa: "Caixa", entregador: "Entregador", super_admin: "Super Admin" };

function Stat({ label, value, tone, testid }) {
  return (
    <div className={`rounded-xl border px-4 py-3 ${tone}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-extrabold tabular-nums">{value}</div>
    </div>
  );
}

export function BloqueiosResumoCards({ resumo }) {
  const alerta = (resumo?.ultimas_24h || 0) > 0;
  return (
    <div className="grid grid-cols-3 gap-3" data-testid="bloqueios-resumo">
      <Stat label="Últimas 24h" value={resumo?.ultimas_24h ?? "—"} testid="bloqueios-24h"
        tone={alerta ? "border-rose-300 bg-rose-50 text-rose-900" : "border-emerald-200 bg-emerald-50 text-emerald-900"} />
      <Stat label="Últimos 7 dias" value={resumo?.ultimos_7d ?? "—"} tone="border-amber-200 bg-amber-50 text-amber-900" testid="bloqueios-7d" />
      <Stat label="Total registrado" value={resumo?.total ?? "—"} tone="border-slate-200 bg-slate-50 text-slate-800" testid="bloqueios-total" />
    </div>
  );
}

export default function BloqueiosPanel({ resumo }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/audit-logs", { params: { action: "acesso_negado", limit: 500 } })
      .then((r) => alive && setRows(r.data))
      .catch(() => alive && setRows([]));
    return () => { alive = false; };
  }, [resumo?.total]);

  return (
    <div className="space-y-4" data-testid="bloqueios-panel">
      <BloqueiosResumoCards resumo={resumo} />
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-rose-600" /> Tentativas de acesso a dados de outra empresa
          </CardTitle>
          <p className="text-xs text-muted-foreground">Cada linha é uma requisição bloqueada pelo servidor (HTTP 403). Repetições do mesmo usuário/IP indicam abuso ou app desatualizado.</p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-40">Data/Hora</TableHead>
              <TableHead>Usuário</TableHead>
              <TableHead className="w-32">IP</TableHead>
              <TableHead>O que tentou</TableHead>
              <TableHead>Empresa alvo</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(rows || []).map((l) => {
                const d = l.details || {};
                return (
                  <TableRow key={l.id} data-testid={`bloqueio-row-${l.id}`}>
                    <TableCell className="text-xs mono whitespace-nowrap align-top">{fmtDateTime(l.created_at)}</TableCell>
                    <TableCell className="align-top text-sm">
                      <div className="font-semibold">{l.user_nome || "—"}</div>
                      <div className="text-[11px] text-muted-foreground">{l.user_email}{d.role ? ` · ${ROLE_LABELS[d.role] || d.role}` : ""}</div>
                    </TableCell>
                    <TableCell className="align-top text-xs mono" data-testid={`bloqueio-ip-${l.id}`}>{d.ip || l.ip || "—"}</TableCell>
                    <TableCell className="align-top text-sm">
                      <div>{d.motivo || "Acesso negado"}</div>
                      {d.path && <div className="text-[11px] text-muted-foreground mono">{d.method} {d.path}</div>}
                    </TableCell>
                    <TableCell className="align-top text-sm">
                      {d.alvo_empresa_nome || (d.alvo_empresa_id ? `#${String(d.alvo_empresa_id).slice(0, 8)}` : "—")}
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows && rows.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground" data-testid="bloqueios-vazio">
                  <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-emerald-500" /> Nenhuma tentativa bloqueada registrada.
                </TableCell></TableRow>
              )}
              {rows === null && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Carregando…</TableCell></TableRow>}
            </TableBody>
          </Table>
          {rows && rows.length > 0 && <Badge variant="outline" className="mt-3">{rows.length} registro(s)</Badge>}
        </CardContent>
      </Card>
    </div>
  );
}
